import emojiesData from "../config/emojies.json";

type EmojiCategory = keyof typeof emojiesData.emojies;

interface Emoji {
  name: string;
  filename: string;
  path: string;
  description: string;
}

/**
 * Get emoji path by name
 * @param name - The emoji name (e.g., "cool", "happy-energy")
 * @returns The emoji image path or undefined if not found
 */
export function getEmojiPath(name: string): string | undefined {
  const allEmojies = Object.values(emojiesData.emojies).flat();
  const emoji = allEmojies.find((e: Emoji) => e.name === name);
  return emoji?.path;
}

/**
 * Get emoji by name
 * @param name - The emoji name
 * @returns The emoji object or undefined if not found
 */
export function getEmoji(name: string): Emoji | undefined {
  const allEmojies = Object.values(emojiesData.emojies).flat();
  return allEmojies.find((e: Emoji) => e.name === name);
}

/**
 * Get all emojies from a specific category
 * @param category - The category name (e.g., "happy", "cool", "sad")
 * @returns Array of emojies in that category
 */
export function getEmojiesByCategory(category: EmojiCategory): Emoji[] {
  return emojiesData.emojies[category] || [];
}

/**
 * Get all available emoji names
 * @returns Array of all emoji names
 */
export function getAllEmojiNames(): string[] {
  return emojiesData.all;
}

/**
 * Search emojies by keyword in name or description
 * @param keyword - The search keyword
 * @returns Array of matching emojies
 */
export function searchEmojies(keyword: string): Emoji[] {
  const allEmojies = Object.values(emojiesData.emojies).flat();
  const lowerKeyword = keyword.toLowerCase();
  return allEmojies.filter(
    (emoji: Emoji) =>
      emoji.name.toLowerCase().includes(lowerKeyword) ||
      emoji.description.toLowerCase().includes(lowerKeyword)
  );
}

/**
 * Get random emoji from a category
 * @param category - The category name (optional, if not provided returns random from all)
 * @returns A random emoji or undefined
 */
export function getRandomEmoji(category?: EmojiCategory): Emoji | undefined {
  const emojies = category
    ? getEmojiesByCategory(category)
    : Object.values(emojiesData.emojies).flat();
  
  if (emojies.length === 0) return undefined;
  
  const randomIndex = Math.floor(Math.random() * emojies.length);
  return emojies[randomIndex];
}

/**
 * Get random emoji excluding full-body emojies
 * @param category - The category name (optional, if not provided returns random from all)
 * @returns A random emoji (excluding full-body ones) or undefined
 */
export function getRandomEmojiExcludingFullBody(category?: EmojiCategory): Emoji | undefined {
  const allEmojies = category
    ? getEmojiesByCategory(category)
    : Object.values(emojiesData.emojies).flat();
  
  // Filter out full-body emojies
  const nonFullBodyEmojies = allEmojies.filter(
    (emoji: Emoji) => !emoji.name.includes("full-body") && !emoji.name.includes("man-full-body")
  );
  
  if (nonFullBodyEmojies.length === 0) return undefined;
  
  const randomIndex = Math.floor(Math.random() * nonFullBodyEmojies.length);
  return nonFullBodyEmojies[randomIndex];
}

export default {
  getEmojiPath,
  getEmoji,
  getEmojiesByCategory,
  getAllEmojiNames,
  searchEmojies,
  getRandomEmoji,
  getRandomEmojiExcludingFullBody,
};

